import React, {useState, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {PageTitle} from '../../../_metronic/layout/core'
import {ServiciosTable} from './components/ServiciosTable'
import {Servicio} from './core/models'
import {fetchWithAuth} from '../../core/utils/fetchWithAuth'

const ServiciosPage: React.FC = () => {
  const navigate = useNavigate()
  
  // Estados para los datos y la carga
  const [servicios, setServicios] = useState<Servicio[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  
  // Estados para la paginación del backend
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [total, setTotal] = useState<number>(0)

  // Función para obtener el token del localStorage
  const getToken = (): string | null => {
    return localStorage.getItem('X-TOKEN')
  }

  // Función para obtener los servicios del backend
  const fetchServicios = async (page: number = 1) => {
    try {
      setLoading(true)
      setError(null)
      
      const token = getToken()
      if (!token) {
        throw new Error('No se encontró el token de autenticación')
      }

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token
        },
        body: JSON.stringify({
          service: 'getConceptos',
          current_page: page
        })
      })

      if (!response.ok) {
        throw new Error(`Error en la petición: ${response.status}`)
      }

      const data = await response.json()
      
      if (data.code === 'OK') {
        setServicios(data.data.conceptos)
        setCurrentPage(data.data.page)
        setTotalPages(data.data.totalPages)
        setTotal(data.data.total)
      } else if (data.code === 'ERROR' && data.returned_message && data.returned_message.includes('JWT expired')) {
        // Token expirado, redirigir al login
        localStorage.removeItem('X-TOKEN')
        navigate('/auth/login')
        return
      } else {
        throw new Error(data.returned_message || 'Error en la respuesta del servidor')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  // Cargar los datos al montar el componente
  useEffect(() => {
    fetchServicios(currentPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage])

  // Función para cambiar de página
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  return (
    <>
      <PageTitle>Listado de Servicios</PageTitle>
      <div className='row g-5 g-xl-8'>
        <div className='col-xl-12'>
          <div className='card'>
            <div className='card-body'>
              {loading && (
                <div className='py-3'>
                  <div className='table-responsive'>
                    <table className='table'>
                      <thead>
                        <tr className='sticky-head'><th colSpan={5}>&nbsp;</th></tr>
                      </thead>
                      <tbody>
                        {Array.from({length: 6}).map((_, i) => (
                          <tr key={`sk-${i}`}>
                            {Array.from({length: 5}).map((__, j) => (
                              <td key={`skc-${i}-${j}`}>
                                <div className='placeholder-glow'><span className='placeholder col-8'></span></div>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {error && (
                <div className='alert alert-danger' role='alert'>
                  {error}
                  <button 
                    className='btn btn-sm btn-outline-primary ms-2'
                    onClick={() => fetchServicios(currentPage)}
                  >
                    Reintentar
                  </button>
                </div>
              )}

              {!loading && !error && (
                <>
                  <ServiciosTable servicios={servicios} />
                  
                  {/* Paginación del backend */}
                  {totalPages > 1 && (
                    <div className='d-flex flex-stack flex-wrap pt-6'>
                      <div className='fs-6 fw-semibold text-gray-700'>
                        Página {currentPage} de {totalPages} - Total: {total} servicios
                      </div>
                      <ul className='pagination'>
                        <li className={`page-item previous ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className='page-link'
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            <i className='previous'></i>
                          </button>
                        </li>
                        {[...Array(totalPages)].map((_, idx) => {
                          const pageNum = idx + 1
                          // Mostrar solo páginas cercanas a la actual
                          if (
                            pageNum === 1 ||
                            pageNum === totalPages ||
                            (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                          ) {
                            return (
                              <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                <button
                                  className='page-link'
                                  onClick={() => handlePageChange(pageNum)}
                                >
                                  {pageNum}
                                </button>
                              </li>
                            )
                          } else if (pageNum === currentPage - 3 || pageNum === currentPage + 3) {
                            return <li key={pageNum} className='page-item disabled'><span className='page-link'>...</span></li>
                          }
                          return null
                        })}
                        <li className={`page-item next ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className='page-link'
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            <i className='next'></i>
                          </button>
                        </li>
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ServiciosPage 